import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // `rounded-md` resolves to calc(var(--radius) - 2px): 0 (square) in the
  // brutalist app, and the terminal scope (--radius: 6px) rounds it to the 4px
  // pill tier — matching the terminal's other pill-radius controls.
  "inline-flex font-bold tracking-wide items-center justify-center gap-2 whitespace-nowrap transition-all focus-visible:outline-hidden focus-visible:ring-1 cursor-pointer focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0 rounded-md",
  {
    variants: {
      variant: {
        default:
          "border-2 bg-brand text-brand-foreground hover:bg-foreground hover:text-background uppercase",
        outline:
          "border-2 bg-background hover:bg-foreground hover:text-background uppercase",
        secondary:
          "border-2 bg-secondary text-secondary-foreground hover:bg-foreground hover:text-background uppercase",
        ghost: "uppercase hover:bg-foreground hover:text-background",
        link: "underline-offset-4 hover:underline hover:decoration-2",
        selected:
          "border-2 border-primary bg-primary text-primary-foreground uppercase hover:bg-foreground hover:text-background hover:border-primary",
        unselected:
          "border-2 bg-background text-foreground uppercase hover:bg-foreground hover:text-background",
      },
      size: {
        sm: "h-8 px-4 text-xs",
        default: "h-10 px-5",
        md: "h-12 px-6",
        lg: "h-14 px-8 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = {
  shouldRenderAsChild?: boolean
  shouldShowShadow?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shouldRenderAsChild = false,
      shouldShowShadow,
      ...props
    },
    ref
  ) => {
    const Comp = shouldRenderAsChild ? Slot.Root : "button"
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          shouldShowShadow &&
            "shadow-sm active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
